import { useState, useMemo, useEffect } from "react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { PropertyFormWrapper } from "./property-form-wrapper";
import { PropertyImageThumbnail } from "@/components/ui/property-image-thumbnail";
import { Edit, Eye, Calendar, User, Star } from "lucide-react";
import type { Id } from "../../convex/_generated/dataModel";
import {
  getStatusTags,
  resolvePropertyTags,
} from "@/lib/property-tags";

interface AgentPropertiesDialogProps {
  open: boolean;
  onClose: () => void;
  agentName: string;
  originalIds: string[];
}

const ITEMS_PER_PAGE = 10;

function formatPropertyType(type: string): string {
  return type
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatDate(timestamp?: number): string {
  if (!timestamp) return "N/A";
  return new Date(timestamp).toLocaleDateString();
}

function formatCreator(createdBy?: string): string {
  if (!createdBy) return "Unknown";
  if (createdBy.includes("@")) {
    return createdBy.split("@")[0];
  }
  return createdBy;
}

export function AgentPropertiesDialog({
  open,
  onClose,
  agentName,
  originalIds,
}: AgentPropertiesDialogProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPropertyId, setSelectedPropertyId] =
    useState<Id<"base_properties"> | null>(null);
  const [editMode, setEditMode] = useState<"edit" | "view" | null>(null);

  const properties = useQuery(api.properties.getPropertiesByAgent, {
    originalIds,
  });

  const tags = useQuery(api.tags.getAllTags, {});

  const paginatedProperties = useMemo(() => {
    if (!properties) return [];
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return properties.slice(start, end);
  }, [properties, currentPage]);

  const totalPages = useMemo(() => {
    if (!properties) return 0;
    return Math.ceil(properties.length / ITEMS_PER_PAGE);
  }, [properties]);

  const handleEdit = (property: any) => {
    setSelectedPropertyId(property.baseProperty._id);
    setEditMode("edit");
  };

  const handleView = (property: any) => {
    setSelectedPropertyId(property.baseProperty._id);
    setEditMode("view");
  };

  const handleCloseEdit = () => {
    setSelectedPropertyId(null);
    setEditMode(null);
  };

  // don't show main dialog when edit mode is active
  const showMainDialog = open && !editMode;

  // reset pagination when dialog opens
  useEffect(() => {
    if (open && !editMode) {
      setCurrentPage(1);
    }
  }, [open, editMode]);

  return (
    <>
      <Dialog
        open={showMainDialog}
        onOpenChange={(isOpen) => {
          // when dialog is being closed (isOpen is false)
          if (!isOpen) {
            // reset edit mode if it was active
            if (editMode) {
              handleCloseEdit();
            }
            // close the main dialog
            onClose();
          }
        }}
      >
        <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Properties by {agentName} ({properties?.length || 0} total)
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {!properties ? (
              <div className="text-center py-8">Loading...</div>
            ) : properties.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  No properties found for this agent.
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Property Name</TableHead>
                        <TableHead>Image</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Area</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedProperties.map((property: any) => (
                        <TableRow key={property.baseProperty._id}>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="font-medium">
                                {property.baseProperty.property_name}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {property.baseProperty.reff_id}
                              </div>
                              {property.baseProperty.tags &&
                                property.baseProperty.tags.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {(() => {
                                      const propertyTags =
                                        resolvePropertyTags(
                                          property.baseProperty.tags,
                                          tags
                                        );

                                      // a SOLD! or Rented listing shows only that tag, otherwise show up to 3 tags
                                      const statusTags =
                                        getStatusTags(propertyTags);
                                      const hasStatusTag =
                                        statusTags.length > 0;
                                      const tagsToShow = hasStatusTag
                                        ? statusTags
                                        : propertyTags.slice(0, 3);

                                      return (
                                        <>
                                          {tagsToShow.map((tag) => (
                                            <Badge
                                              key={tag._id}
                                              variant={
                                                hasStatusTag
                                                  ? "destructive"
                                                  : "outline"
                                              }
                                              className={`text-xs ${
                                                hasStatusTag
                                                  ? "font-semibold"
                                                  : ""
                                              }`}
                                            >
                                              {tag.tag_name}
                                            </Badge>
                                          ))}
                                          {!hasStatusTag &&
                                            propertyTags.length > 3 && (
                                              <Badge
                                                variant="outline"
                                                className="text-xs"
                                              >
                                                +{propertyTags.length - 3}
                                              </Badge>
                                            )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <PropertyImageThumbnail
                              images={property.baseProperty.images || []}
                              size="md"
                              showCount={true}
                              maxImages={1}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {(() => {
                                const propertyTypes =
                                  property.propertyTypes || [];
                                const hasMultipleTypes =
                                  propertyTypes.length > 1;
                                const firstType =
                                  propertyTypes[0] || property.propertyType;

                                if (!firstType) return null;

                                return (
                                  <Badge variant="outline" className="text-xs">
                                    {formatPropertyType(firstType)}
                                    {hasMultipleTypes && (
                                      <span className="ml-1 text-muted-foreground">
                                        ({propertyTypes.length}+)
                                      </span>
                                    )}
                                  </Badge>
                                );
                              })()}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {property.area?.area_name || "Unknown"}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {property.baseProperty.featured && (
                                <Badge variant="default" className="text-xs">
                                  <Star className="h-3 w-3 mr-1" />
                                  Featured
                                </Badge>
                              )}
                              {property.baseProperty.draft ? (
                                <Badge variant="secondary" className="text-xs">
                                  Draft
                                </Badge>
                              ) : (
                                <Badge variant="default" className="text-xs">
                                  Published
                                </Badge>
                              )}
                              {property.baseProperty.hidden && (
                                <Badge
                                  variant="destructive"
                                  className="text-xs"
                                >
                                  Hidden
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                {formatDate(property.baseProperty.created_at)}
                              </div>
                              {property.baseProperty.created_by && (
                                <div
                                  className="flex items-center gap-1 text-xs text-muted-foreground"
                                  title={`Created by: ${property.baseProperty.created_by}`}
                                >
                                  <User className="h-3 w-3" />
                                  {formatCreator(
                                    property.baseProperty.created_by
                                  )}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(property)}
                                title="Edit property"
                                className="active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-muted-foreground">
                      Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{" "}
                      {Math.min(
                        currentPage * ITEMS_PER_PAGE,
                        properties.length
                      )}{" "}
                      of {properties.length} properties
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setCurrentPage((p) => Math.max(1, p - 1))
                        }
                        disabled={currentPage === 1}
                        className="active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setCurrentPage((p) => Math.min(totalPages, p + 1))
                        }
                        disabled={currentPage === totalPages}
                        className="active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {editMode && selectedPropertyId && (
        <PropertyFormWrapper
          open={true}
          onClose={handleCloseEdit}
          mode={editMode}
          propertyId={selectedPropertyId}
        />
      )}
    </>
  );
}
